'use client';
import React from 'react';
import { Row } from '@/ui/primatives';
import Tag from '@/ui/Tag';
import * as Icon from '@/ui/Icon';
import Link from 'next/link';
import { IRepository } from '@/types';
import { IEvaluation } from '@/lib/evaluationsApi';
import { hasBranchAdvanced } from '../../lib';
import { AlertFillIcon } from '@primer/octicons-react';
import * as EvaluationTag from '../../EvaluationTag';
import { colors } from '@/styles';

import { getTargetUrl, getSourceUrl } from '../../lib';

const BranchAdvancedTooltipContent = () => (
  <>
    The branch has been advanced since the evaluation was run.
    <br />
    this link will take you to the version of the
    <br /> file created by the evaluation.
  </>
);

const BranchTag: React.FC<{
  branch: string;
  hasAdvanced?: boolean;
}> = ({ branch, hasAdvanced }) => {
  return (
    <Tag
      variant={hasAdvanced ? 'warning' : 'info'}
      small
      truncate
      tooltip={hasAdvanced ? { content: <BranchAdvancedTooltipContent /> } : undefined}
    >
      <Icon.Branch size="12px" />
      <span className="text-line-truncate">{branch}</span>
      {hasAdvanced && <AlertFillIcon fill={colors.semantic.notice.primary} size={14} />}
    </Tag>
  );
};

const SourceFileLinkWrapper: React.FC<{
  repository?: IRepository;
  evaluation: IEvaluation;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ repository, evaluation, children, style }) =>
  repository ? (
    <Link href={getSourceUrl(repository, evaluation)} className="link-underline" style={style}>
      {children}
    </Link>
  ) : (
    <>{children}</>
  );

const TargetFileLinkWrapper: React.FC<{
  repository: IRepository;
  evaluation: IEvaluation;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ repository, evaluation, children, style }) => {
  return repository ? (
    <Link href={getTargetUrl(repository, evaluation)} className="link-underline" style={style}>
      {children}
    </Link>
  ) : (
    <>{children}</>
  );
};

const SourceFileInfo: React.FC<{
  repository?: IRepository;
  evaluation: IEvaluation;
  className?: string;
  style?: React.CSSProperties;
}> = ({ repository, evaluation, className, style }) => {
  const resource = evaluation?.resource;

  return (
    <Row gap={5} alignItems="center" className={className} style={style}>
      <BranchTag branch={resource.version} />
      <div className="truncate">
        <SourceFileLinkWrapper repository={repository} evaluation={evaluation}>
          {resource.path}
        </SourceFileLinkWrapper>
      </div>
    </Row>
  );
};

const TargetFileInfo: React.FC<{
  evaluation: IEvaluation;
  repository: IRepository;
  className?: string;
  style?: React.CSSProperties;
}> = ({ evaluation, repository, className, style }) => {
  const isBranchAdvanced = hasBranchAdvanced(evaluation, repository);
  return (
    <Row gap={5} alignItems="center" className={className} justifyContent="flex-end">
      {evaluation.closing_commit ? (
        <>
          <BranchTag
            hasAdvanced={isBranchAdvanced}
            branch={evaluation.conflict_target_branch || evaluation.target_branch}
          />
          <div className="truncate">
            <TargetFileLinkWrapper repository={repository} evaluation={evaluation}>
              {evaluation.target_path}
            </TargetFileLinkWrapper>
          </div>
        </>
      ) : (
        <>
          <EvaluationTag.Target repository={repository} evaluation={evaluation} />
          <div className="truncate">
            <TargetFileLinkWrapper repository={repository} evaluation={evaluation}>
              {evaluation.target_path}
            </TargetFileLinkWrapper>
          </div>
        </>
      )}
    </Row>
  );
};

export { SourceFileInfo, TargetFileInfo };
